// ======================================================================
// File Name       : BoxAction
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

import { openModal, hideModal } from './ModalAction';
import * as boxActionCreators from './actionCreators/box';
import * as userAttributeActions from './UserAttributeAction';
import * as containerActions from './ContainerAction';
import * as CourseActions from './CourseAction';
import * as userAttributeRequests from './httpRequests/userAttribute';
import * as boxRequests from './httpRequests/box';
import * as itemRequests from './httpRequests/item';
import * as thumbnail from './httpRequests/thumbnail';
import * as ModalIds from '../constants/ModalIds';
import { MODE, STRUCTURE_TYPE, SELECT_TARGET_DATA } from '../constants';
import { USER_ATTRIBUTE_TYPE } from '../constants/UserAttributeConstants';
import { END_CONDITION_TYPE_FOR_BOX, CHOSEN_THUMBNAIL_BOX } from '../constants/BoxConstants';
import { END_CONDITION_TYPE_FOR_ITEM, START_CONDITION_TYPE_FOR_BOX } from '../constants/ItemConstant';
import { ITEM_IMAGE_TYPE } from '../constants/InitialImageConstants';

export function fetchBoxList(param) {
  return dispatch =>
    boxRequests
      .postBoxGetList({
        searchInfo: {
          ...param,
        },
        selectTargetData: SELECT_TARGET_DATA.DATA,
      })
      .then(({ recordNum, boxList }) => {
        dispatch(boxActionCreators.receiveBoxList(recordNum, boxList));
      });
}

export function initDataForRegisterBoxDialog(boxSecernId) {
  return async (dispatch) => {
    const boxApiParam = {
      boxList: [boxSecernId],
    };

    dispatch(fetchBoxList(boxApiParam)).then(() => {
      dispatch(openModal(ModalIds.BOX_REGISTER_EDIT));
      dispatch(boxActionCreators.initDataForBoxRegisterDialog(boxSecernId));
      dispatch(userAttributeActions.initUserAttributeEditing(boxSecernId));
      dispatch(
        containerActions.initDataForItemTreeDialog(
          boxSecernId,
          STRUCTURE_TYPE.BOX,
          true
        )
      );
    });
  };
}

export function initDataForDetailEditConfirmDialog(boxSecernId, childItemSercernIdList) {
  return (dispatch, getState) => {
    dispatch(openModal(ModalIds.BOX_DETAIL));
    const requests = [];

    const boxApiParam = {
      boxList: [boxSecernId],
    };

    // fetch box's info
    requests.push(dispatch(fetchBoxList(boxApiParam)));

    // fetch user attribute
    requests.push(
      dispatch(userAttributeActions.initUserAttributeEditing(USER_ATTRIBUTE_TYPE.BOX, boxSecernId)),
    );

    Promise.all(requests).then(() => {
      dispatch(containerActions.initDataForItemTreeDialog(boxSecernId, STRUCTURE_TYPE.BOX, false)); // sprint_20230522_tma
      dispatch(
        boxActionCreators.initDataForDetailEditConfirmDialog(boxSecernId, childItemSercernIdList),
      );
    });
  };
}

export function handleInputChange(payload) {
  return dispatch => dispatch(boxActionCreators.handleInputChange(payload));
}

export function deleteBox(boxSecernId) {
  return async (dispatch) => {
    // save course info
    await boxRequests.postBoxEdit(MODE.DELETE_MODE, {
      boxSecernId,
    });
    dispatch(containerActions.deleteBoxStructure(boxSecernId));
  };
}

export function saveBox() {
  return async (dispatch, getState) => {
    const { box, container, state } = getState();
    const { editingBox, editType, childItemSercernIdList } = box;
    const { structures, selectedItemSecernIdList } = container;
    /**
     * Change end condition when edit box
     */
    const udpateEndCondition =
      editType === MODE.EDIT_MODE && editingBox.endConditionTypeForBox == END_CONDITION_TYPE_FOR_BOX.PROGRESS;
    if (udpateEndCondition && childItemSercernIdList && childItemSercernIdList.length) {
      editingBox.progressRatioForBoxEnd = 100;
      editingBox.itemListForBoxEnd = childItemSercernIdList;
    }
    // sprint_20230522 START
    if (editingBox.startConditionTypeForBox === START_CONDITION_TYPE_FOR_BOX.ACHIEVEMENT_RATE_CONDITION) {
      const progressRatioForBoxStart = editingBox.progressRatioForBoxStart ? editingBox.progressRatioForBoxStart : 0;
      editingBox.progressRatioForBoxStart = progressRatioForBoxStart;
    } else {
      delete editingBox.progressRatioForBoxStart;
    }
    // sprint_20230522 END

    let postBoxEditData = { ...editingBox };
    if (editType === MODE.NEW_MODE) {
      /**
     * Find parent course/box secern id
     */
      const parentStructure = structures.bySecernId[editingBox.boxSecernId];
      const { parentCourseSecernId, parentBoxSecernId, structureType } = parentStructure;
      if (structureType === STRUCTURE_TYPE.COURSE) {
        postBoxEditData = {
          ...editingBox,
          parentCourseSecernId: parentCourseSecernId || postBoxEditData.boxSecernId,
        };
      } else if (structureType === STRUCTURE_TYPE.BOX) {
        postBoxEditData = {
          ...editingBox,
          parentBoxSecernId: postBoxEditData.boxSecernId,
        };
      }
      delete postBoxEditData.boxSecernId;
    }

    // Remove GUI, un-used variable
    if (postBoxEditData) {
      if (postBoxEditData.createdAt) {
        delete postBoxEditData.createdAt;
      }
      if (postBoxEditData.updatedAt) {
        delete postBoxEditData.updatedAt;
      }
      if (editType !== MODE.NEW_MODE) {
        delete postBoxEditData.parentCourseSecernId;
        delete postBoxEditData.parentBoxSecernId;
        delete postBoxEditData.adaptiveSlotType;
        delete postBoxEditData.drillRecomendationFlag;
        delete postBoxEditData.maxRecommendLearningElementNumber;
        delete postBoxEditData.maxRecommendContentNumber;
        delete postBoxEditData.drillRecommendFlag;
        delete postBoxEditData.videoRecommendFlag;
        delete postBoxEditData.documentRecommendFlag;
        delete postBoxEditData.linkRecommendFlag;
      }
      // sprint_20230522 START
      if (postBoxEditData.startConditionTypeForBox !== START_CONDITION_TYPE_FOR_BOX.NO_CONDITION) {
        postBoxEditData.itemListForBoxStart = selectedItemSecernIdList;
      }
      // sprint_20230522 END
    }

    // save course info
    const { boxSecernId } = await boxRequests.postBoxEdit(editType, postBoxEditData);

    const data = { ...postBoxEditData };
    const uploadThumnail = {
      thumbnailTargetType: 1,
      thumbnailTargetSecernId: boxSecernId,
      imageFileSelectType: data.imageFileSelectType,
      imageFileUpload: data.imageUploadFile,
    };
    // box user attribute

    await dispatch(userAttributeActions.saveUserAttribute(USER_ATTRIBUTE_TYPE.BOX, boxSecernId, editType));
    if (data && data.imageFileSelectType && data.imageFileSelectType == CHOSEN_THUMBNAIL_BOX.MANUAL && data.imageUploadFile) {
      await thumbnail.editThumnail(uploadThumnail);
    }
    if (editType == MODE.EDIT_MODE && data.imageFileSelectType == CHOSEN_THUMBNAIL_BOX.DEFAULT) {
      const uploadThumnailModeEdit = {
        thumbnailTargetType: 1,
        thumbnailTargetSecernId: boxSecernId,
        imageFileSelectType: CHOSEN_THUMBNAIL_BOX.DEFAULT,
      };
      await thumbnail.editThumnail(uploadThumnailModeEdit);
    }
    dispatch(containerActions.editBoxStructure(editType, boxSecernId, postBoxEditData));
    return dispatch(hideModal());
  };
}

export function changeData() {
  return boxActionCreators.changeData();
}

export function getDefaultThumbnail() {
  return async (dispatch) => {
    const searchInfo = {
      imageTypeList: [ITEM_IMAGE_TYPE.BOX],
    };
    const thumbnailDefault = await thumbnail.defaultThumbnail({ searchInfo });
    return dispatch(boxActionCreators.getThumbnailDefault(thumbnailDefault));
  };
}

export function saveDetailSettingState(name, value) {
  return boxActionCreators.saveDetailSettingState(name, value);
}
